import React, { useCallback, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import CustomButton from "./CustomButton";
import { useResume } from "../../../services/api-client/useResume";
//import { ResumeProfile } from "../../../DataModels/DataModels";

const ONE: number = 1;
const ERROR_VISIBLE_DURATION_MS: number = 2200;
const KILOBYTE: number = 2 ** 10;
const GIGABYTE: number = 2 ** 30;

// Define types for the ResumeDropzone component
interface ResumeDropzoneProps {
	//fileMutation: useMutationResult<any, any, { File }, unknown>;
	uploadEndpoint?: string;
	onError?: (error: Error) => void; //TODO: Merge OnError with OnSuccess - NotifyUser{u:UserMessage}
	onSuccess?: (response: string) => void;
	className?: string;
}

// Allowed file types
const ACCEPTED_FILE_TYPES = {
	"text/plain": [".txt"],
	"text/markdown": [".md"],
	"application/pdf": [".pdf"],
	"application/msword": [".doc"],
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
		".docx"
	]
};

const shortErrorText = {
	"file-invalid-type": "Please upload txt, md, pdf, doc, or docx files.",
	"file-too-large": "File is too large. Please upload a smaller file.",
	"file-too-small": "File is too small. Please upload a larger file.",
	"too-many-files": "You can only upload one file at a time."
};

export const ResumeDropzone: React.FC<ResumeDropzoneProps> = ({
	onError,
	onSuccess,
	className = ""
}) => {
	const { uploadResume } = useResume();
	const [isInvalid, setIsInvalid] = useState(false);
	const [errorText, setErrorText] = useState("");
	const [isFocused, setIsFocused] = useState<boolean>(false);

	const [isUploading, setIsUploading] = useState(false);
	//const [uploadProgress, setUploadProgress] = useState(0);

	// const performProgress = ()=>{
	// 	console.log(`progress`);
	// 	setUploadProgress((prev)=>{
	// 		let rVal = prev+1;
	// 		if (rVal>100) rVal = 75;
	// 		return rVal;
	// 	});
	//}

	// Function to upload the file to the endpoint
	const uploadFile = useCallback(
		async (file: File) => {
			setIsInvalid(false); //Clear all errors
			//console.log(file);
			setIsUploading(true); //Pre for Ux to show uploading
			//setUploadProgress(0);

			//const rescursiveTimer = setInterval(performProgress,PROGRESS_TIMER_DELAY);

			uploadResume
				.mutateAsync(file)
				.then((data: string) => {
					onSuccess?.(data);
				})
				.catch((error: Error) => {
					onError?.(error);
				})
				.finally(() => {
					setIsUploading(false);
					//clearInterval(rescursiveTimer);
				});
		},
		[onError, onSuccess, uploadResume]
	);

	const handleFocus = (): void => {
		setIsFocused(true);
	};

	const handleBlur = (): void => {
		setIsFocused(false);
	};

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			acceptedFiles.forEach((item) => {
				uploadFile(item);
			});
		},
		[uploadFile]
	);

	const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
		if (fileRejections.length > 0) {
			setIsInvalid(true);
			setErrorText(
				shortErrorText["too-many-files" as keyof typeof shortErrorText]
			);
			setTimeout(() => setIsInvalid(false), ERROR_VISIBLE_DURATION_MS);
		}

		fileRejections.forEach(({ file, errors }) => {
			console.log(`File:[${file.name}]\n`);
			errors.forEach(({ message, code }) => {
				console.log(
					`Code:[${code}] Message:[${shortErrorText[code as keyof typeof shortErrorText]}]`
				);
				setIsInvalid(true);
				setErrorText(shortErrorText[code as keyof typeof shortErrorText]);
				setTimeout(() => setIsInvalid(false), ERROR_VISIBLE_DURATION_MS);
			});
		});
	}, []);
	// Configure dropzone
	const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
		accept: ACCEPTED_FILE_TYPES,
		noClick: true, // We'll handle clicks on the button separately
		noKeyboard: false,
		maxFiles: ONE,
		maxSize: GIGABYTE,
		minSize: KILOBYTE,
		onDrop: onDrop,
		onDropRejected: onDropRejected
	});

	// Handle button click to open file dialog
	const handleButtonClick = () => {
		open(); // This opens the file dialog
	};

	return (
		<div
			onFocus={handleFocus}
			onBlur={handleBlur}
			{...getRootProps()}
			// className={`relative ${isDragActive ? 'bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg' : ''} ${className}`}
		>
			<input {...getInputProps()} />

			<CustomButton
				text={
					isDragActive
						? "Drop your resume file here..."
						: isUploading
							? "Processing Resume"
							: "Upload Resume"
				}
				//title={isUploading ? `Uploading ${uploadProgress}%` : ""}
				rightIcon="Upload"
				onClick={handleButtonClick}
				className={`${className} ${isUploading ? "text-primary-inactive animate-pulse" : "text-primary-active"}  ${
					isDragActive
						? "border-primary-active bg-primary-accent"
						: "border-primary-inactive"
				}`}
			/>

			{/* Validation message container with fixed height */}
			<div
				onClick={handleButtonClick}
				className={`cursor-default text-sm px-6 mt-1 transition-opacity ease-in-out duration-300 min-h-6 ${
					isInvalid ? "opacity-100" : "opacity-0"
				} ${isFocused ? "text-error-active" : "text-error-inactive"}`}
			>
				{errorText}
			</div>
		</div>
	);
};

//export default ResumeDropzone;
